import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  Restaurant as RestaurantIcon,
  Euro as EuroIcon,
} from '@mui/icons-material';

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  image: string;
  available: boolean;
}

interface CartItem {
  item: MenuItem;
  quantity: number;
}

const sampleMenuItems: MenuItem[] = [
  {
    id: '1',
    name: 'Poulet Roti',
    description: 'Geroosterde kip met kruiden en groenten',
    price: 18.50,
    category: 'Hoofdgerechten',
    image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Poulet+Roti',
    available: true
  },
  {
    id: '2',
    name: 'Poulet Frites',
    description: 'Kip met frietjes en mayonaise',
    price: 15.90,
    category: 'Hoofdgerechten',
    image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Poulet+Frites',
    available: true
  },
  {
    id: '3',
    name: 'Poulet Vol-au-vent',
    description: 'Kip in vol-au-vent met champignons',
    price: 16.50,
    category: 'Hoofdgerechten',
    image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Vol-au-vent',
    available: true
  },
  {
    id: '4',
    name: 'Frietjes',
    description: 'Verse frietjes met mayonaise',
    price: 4.50,
    category: 'Bijgerechten',
    image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Frietjes',
    available: true
  },
  {
    id: '5',
    name: 'Salade',
    description: 'Verse groene salade met dressing',
    price: 6.50,
    category: 'Bijgerechten',
    image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Salade',
    available: true
  },
  {
    id: '6',
    name: 'Tiramisu',
    description: 'Klassieke tiramisu met koffie',
    price: 7.50,
    category: 'Desserts',
    image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Tiramisu',
    available: true
  },
  {
    id: '7',
    name: 'Cola',
    description: 'Frisdrank 33cl',
    price: 3.50,
    category: 'Dranken',
    image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Cola',
    available: true
  },
  {
    id: '8',
    name: 'Bier',
    description: 'Belgisch bier 33cl',
    price: 4.50,
    category: 'Dranken',
    image: 'https://via.placeholder.com/150x100/000000/FFFFFF?text=Bier',
    available: true
  }
];

interface TakeawayViewProps {
  onBack: () => void;
}

const TakeawayView: React.FC<TakeawayViewProps> = ({ onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutForm, setCheckoutForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postcode: '',
    pickupTime: '',
    notes: '',
    paymentMethod: 'card'
  });

  const categories = ['all', ...Array.from(new Set(sampleMenuItems.map(item => item.category)))];

  const filteredItems = sampleMenuItems.filter(item =>
    (selectedCategory === 'all' || item.category === selectedCategory) &&
    (item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
     item.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const addToCart = (item: MenuItem) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.item.id === item.id);
      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.item.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      } else {
        return [...prevCart, { item, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (itemId: string) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.item.id === itemId);
      if (existingItem && existingItem.quantity > 1) {
        return prevCart.map(cartItem =>
          cartItem.item.id === itemId
            ? { ...cartItem, quantity: cartItem.quantity - 1 }
            : cartItem
        );
      } else {
        return prevCart.filter(cartItem => cartItem.item.id !== itemId);
      }
    });
  };

  const getTotalPrice = () => {
    return cart.reduce((total, cartItem) => total + (cartItem.item.price * cartItem.quantity), 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, cartItem) => total + cartItem.quantity, 0);
  };

  const handleCheckout = () => {
    setShowCheckout(true);
  };

  const handleSubmitOrder = async () => {
    try {
      const orderData = {
        items: cart.map(item => ({
          id: item.item.id,
          name: item.item.name,
          quantity: item.quantity,
          price: item.item.price
        })),
        totalAmount: getTotalPrice(),
        customerInfo: checkoutForm,
        pickupTime: checkoutForm.pickupTime
      };
      
      // TODO: Send order to backend
      console.log('Submitting order:', orderData);
      
      // Reset form and close checkout
      setCheckoutForm({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postcode: '',
        pickupTime: '',
        notes: '',
        paymentMethod: 'card'
      });
      setCart([]);
      setShowCheckout(false);
      setShowCart(false);
      
      alert('Bestelling succesvol geplaatst! Je ontvangt een bevestiging per e-mail.');
    } catch (error) {
      console.error('Error submitting order:', error);
      alert('Er is een fout opgetreden bij het plaatsen van je bestelling.');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={onBack} sx={{ color: '#000000' }}>
              <ArrowBackIcon />
            </IconButton>
            <RestaurantIcon sx={{ fontSize: 32, color: '#000000' }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000000' }}>
              Takeaway
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<CartIcon />}
            onClick={() => setShowCart(!showCart)}
            sx={{ 
              backgroundColor: '#000000',
              color: '#ffffff',
              '&:hover': { backgroundColor: '#333333' }
            }}
          >
            Winkelwagen ({getTotalItems()})
          </Button>
        </Toolbar>
      </AppBar>

      {/* Search and Filters */}
      <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                placeholder="Zoek gerechten..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ minWidth: { md: 300 } }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {categories.map((category) => (
                  <Chip
                    key={category}
                    label={category === 'all' ? 'Alles' : category}
                    onClick={() => setSelectedCategory(category)}
                    color={selectedCategory === category ? 'primary' : 'default'}
                    variant={selectedCategory === category ? 'filled' : 'outlined'}
                    sx={{ fontSize: '0.8rem' }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex' }}>
        {/* Menu Items */}
        <Box sx={{ 
          flexGrow: 1,
          p: 2,
          overflow: 'auto'
        }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3 }}>
              {filteredItems.map((item) => (
                <Card key={item.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardMedia
                    component="img"
                    height="140"
                    image={item.image}
                    alt={item.name}
                  />
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1 }}>
                      {item.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000000' }}>
                        €{item.price.toFixed(2)}
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => addToCart(item)}
                        disabled={!item.available}
                        sx={{ 
                          backgroundColor: '#000000',
                          color: '#ffffff',
                          '&:hover': { backgroundColor: '#333333' }
                        }}
                      >
                        Toevoegen
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Container>
        </Box>

        {/* Cart Sidebar */}
        {showCart && (
          <Box sx={{ 
            width: isMobile ? '100%' : 400,
            borderLeft: isMobile ? 'none' : '1px solid #e0e0e0',
            backgroundColor: '#ffffff',
            p: 2
          }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              Winkelwagen
            </Typography>
            
            {cart.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                Je winkelwagen is leeg
              </Typography>
            ) : (
              <>
                <List sx={{ mb: 2 }}>
                  {cart.map((cartItem) => (
                    <ListItem key={cartItem.item.id} sx={{ px: 0 }}>
                      <ListItemText
                        primary={cartItem.item.name}
                        secondary={`€${cartItem.item.price.toFixed(2)} x ${cartItem.quantity}`}
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => removeFromCart(cartItem.item.id)}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
                            {cartItem.quantity}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => addToCart(cartItem.item)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Totaal:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000000' }}>
                    €{getTotalPrice().toFixed(2)}
                  </Typography>
                </Box>
                
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<EuroIcon />}
                  onClick={handleCheckout}
                  sx={{ 
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#333333' }
                  }}
                >
                  Afrekenen
                </Button>
              </>
            )}
          </Box>
        )}

        {/* Checkout Dialog */}
        <Dialog 
          open={showCheckout} 
          onClose={() => setShowCheckout(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxWidth: 800,
              width: '100%'
            }
          }}
        >
          <DialogTitle sx={{ 
            fontWeight: 'bold', 
            textAlign: 'center',
            pb: 1,
            '& .MuiTypography-root': {
              fontSize: '1rem',
              color: '#000000'
            }
          }}>
            AFREKENEN
          </DialogTitle>
          <DialogContent sx={{ px: 3, pb: 3 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 4 }}>
              {/* Order Summary */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Bestelling Overzicht
                </Typography>
                <List sx={{ mb: 2 }}>
                  {cart.map((cartItem) => (
                    <ListItem key={cartItem.item.id} sx={{ px: 0 }}>
                      <ListItemText
                        primary={cartItem.item.name}
                        secondary={`€${cartItem.item.price.toFixed(2)} x ${cartItem.quantity}`}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        €{(cartItem.item.price * cartItem.quantity).toFixed(2)}
                      </Typography>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Totaal:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000000' }}>
                    €{getTotalPrice().toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* Customer Information */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Je Gegevens
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="Voornaam"
                    value={checkoutForm.firstName}
                    onChange={(e) => setCheckoutForm({...checkoutForm, firstName: e.target.value})}
                    fullWidth
                    size="small"
                    required
                  />
                  <TextField
                    label="Achternaam"
                    value={checkoutForm.lastName}
                    onChange={(e) => setCheckoutForm({...checkoutForm, lastName: e.target.value})}
                    fullWidth
                    size="small"
                    required
                  />
                  <TextField
                    label="E-mail"
                    type="email"
                    value={checkoutForm.email}
                    onChange={(e) => setCheckoutForm({...checkoutForm, email: e.target.value})}
                    fullWidth
                    size="small"
                    required
                  />
                  <TextField
                    label="Telefoon"
                    value={checkoutForm.phone}
                    onChange={(e) => setCheckoutForm({...checkoutForm, phone: e.target.value})}
                    fullWidth
                    size="small"
                    required
                  />
                </Box>
                
                <TextField
                  label="Adres"
                  value={checkoutForm.address}
                  onChange={(e) => setCheckoutForm({...checkoutForm, address: e.target.value})}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
                
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="Stad"
                    value={checkoutForm.city}
                    onChange={(e) => setCheckoutForm({...checkoutForm, city: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Postcode"
                    value={checkoutForm.postcode}
                    onChange={(e) => setCheckoutForm({...checkoutForm, postcode: e.target.value})}
                    fullWidth
                    size="small"
                  />
                </Box>

                <TextField
                  label="Ophaaltijd"
                  type="time"
                  value={checkoutForm.pickupTime}
                  onChange={(e) => setCheckoutForm({...checkoutForm, pickupTime: e.target.value})}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />

                <TextField
                  label="Opmerkingen"
                  multiline
                  rows={2}
                  value={checkoutForm.notes}
                  onChange={(e) => setCheckoutForm({...checkoutForm, notes: e.target.value})}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />

                {/* Payment Method */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Betaalmethode
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="radio"
                        id="card"
                        name="paymentMethod"
                        value="card"
                        checked={checkoutForm.paymentMethod === 'card'}
                        onChange={(e) => setCheckoutForm({...checkoutForm, paymentMethod: e.target.value})}
                        style={{ marginRight: '8px' }}
                      />
                      <label htmlFor="card" style={{ fontSize: '0.875rem' }}>
                        Kaart
                      </label>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="radio"
                        id="cash"
                        name="paymentMethod"
                        value="cash"
                        checked={checkoutForm.paymentMethod === 'cash'}
                        onChange={(e) => setCheckoutForm({...checkoutForm, paymentMethod: e.target.value})}
                        style={{ marginRight: '8px' }}
                      />
                      <label htmlFor="cash" style={{ fontSize: '0.875rem' }}>
                        Contant
                      </label>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              variant="contained"
              onClick={handleSubmitOrder}
              disabled={!checkoutForm.firstName || !checkoutForm.lastName || !checkoutForm.email || !checkoutForm.phone || !checkoutForm.pickupTime}
              fullWidth
              sx={{ 
                backgroundColor: '#8B4513',
                color: '#ffffff',
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: '#A0522D' },
                '&:disabled': { backgroundColor: '#ccc' }
              }}
            >
              Bestelling Plaatsen - €{getTotalPrice().toFixed(2)}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default TakeawayView;
